import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { RotatedImg, RotatedFrame } from './RotatedScan';
import { pageRotation } from '../utils/pageRotation';

// The original scan of a document (DocumentViewPage view mode), in one of two
// shells around the same toolbar + page:
//   variant 'pane'       — desktop: a sticky, drag-resizable side pane beside
//                          the digital copy (render it as the split's last child)
//   variant 'fullscreen' — a full-screen layer over the page: the phone's only
//                          shell, and on desktop the pane's "מסך מלא"
//
// Desktop moves between the two: the pane passes `onMaximize` (its מסך מלא
// button, and a click on an image page), the full-screen layer `onDock`
// (its לצד הנתונים button, and Esc). Without `onDock`, Esc closes.
//
// `url` is the page's object URL (fetched by the page, which owns the JWT
// fetch and revokes stale blobs); empty while in flight.
//
// Each page shows turned by its saved `rotation` (RotatedScan.jsx). `onRotate`
// (admins only — the caller decides) adds the ⟳ button; it gets the current
// file and saves the next quarter turn.
//
// A PDF is shown in an iframe, which iOS Safari renders poorly (often page 1
// only, no pinch-zoom) — so the fullscreen shell also offers the phone's own
// viewer ("פתיחה בחלון נפרד"), a real <a> the user taps, which keeps the gesture
// chain intact where a script-driven window.open is blocked.
export const isPdfPath = (p) => /\.pdf(\?|$)/i.test(p || '');

// Desktop pane width. By DEFAULT the scan takes most of the split: the data
// column (rows, while the pane is open) holds at clamp(380px, 40%, 480px) in
// CSS and the scan gets the rest — whoever opened it came to read it, and at
// the old 42% an invoice's small print rendered at ~6–7px. A width the user
// drags (% of the split) is remembered and wins; double-click goes back to
// the default. Same interaction as the edit screen's ReviewDocPane, under its
// own key. `-v2`: widths saved under the old 42% default were never chosen by
// anyone, so they're left behind once.
const PANE_WIDTH_KEY = 'doc-view-pane-width-v2';
const PANE_MIN = 25;
const PANE_MAX = 75;
const DATA_MIN_PX = 380;   // the data column never gets narrower than this
const SPLIT_GAP_PX = 24;   // .dv-layout.is-split gap
const round1 = (n) => Math.round(n * 10) / 10;
// Clamp to the % limits AND keep the data column at DATA_MIN_PX or more.
const clampWidth = (n, splitWidth) => {
  const dataFloorMax = splitWidth > 0 ? ((splitWidth - DATA_MIN_PX - SPLIT_GAP_PX) / splitWidth) * 100 : PANE_MAX;
  return round1(Math.min(PANE_MAX, dataFloorMax, Math.max(PANE_MIN, n)));
};
const readSavedWidth = () => {
  try {
    const saved = parseFloat(localStorage.getItem(PANE_WIDTH_KEY));
    return Number.isFinite(saved) ? Math.min(PANE_MAX, Math.max(PANE_MIN, saved)) : null;
  } catch { return null; } // storage blocked — the default is fine
};
const saveWidth = (w) => {
  try {
    if (w == null) localStorage.removeItem(PANE_WIDTH_KEY);
    else localStorage.setItem(PANE_WIDTH_KEY, String(Math.round(w)));
  } catch (err) {
    // A per-viewer nicety — the pane still works, the width just won't stick.
    console.warn('[doc-pane] could not save width', err);
  }
};

// The pane is the split's LAST child — the left column in RTL — so it widens
// as the pointer moves right, measured from the split container's left edge.
// widthPct null = the CSS default.
function usePaneWidth(paneRef) {
  const [widthPct, setWidthPct] = useState(readSavedWidth);
  const [resizing, setResizing] = useState(false);
  const widthRef = useRef(widthPct);
  widthRef.current = widthPct;
  const splitRect = () => paneRef.current?.parentElement?.getBoundingClientRect();
  // The pane's actual share, for keyboard steps and aria when on the default.
  const currentPct = () => {
    if (widthPct != null) return widthPct;
    const rect = splitRect();
    const pane = paneRef.current?.getBoundingClientRect();
    return rect && pane && rect.width > 0 ? round1((pane.width / rect.width) * 100) : 60;
  };

  // While dragging, listen on the WINDOW (as ReviewDocPane does), not the
  // handle: a release can land anywhere — over the PDF iframe, outside the
  // pane, or never arrive at all (alt-tab), and a drag that misses its end
  // leaves the pane stuck resizing. pointercancel and blur end it too.
  useEffect(() => {
    if (!resizing) return undefined;
    const onMove = (e) => {
      const rect = splitRect();
      if (!rect || rect.width <= 0) return;
      const pct = clampWidth(((e.clientX - rect.left) / rect.width) * 100, rect.width);
      widthRef.current = pct;
      setWidthPct(pct);
    };
    const onEnd = () => { setResizing(false); saveWidth(widthRef.current); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    window.addEventListener('blur', onEnd);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      window.removeEventListener('blur', onEnd);
    };
  }, [resizing]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProps = {
    role: 'separator',
    'aria-orientation': 'vertical',
    'aria-label': 'רוחב המסמך המקורי',
    'aria-valuemin': PANE_MIN,
    'aria-valuemax': PANE_MAX,
    'aria-valuenow': Math.round(currentPct()),
    tabIndex: 0,
    title: 'גרירה לשינוי רוחב • לחיצה כפולה לאיפוס',
    // Pointer capture keeps the moves coming while the cursor crosses the PDF
    // iframe (which would otherwise swallow them); the window listeners above
    // do the rest.
    onPointerDown: (e) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setResizing(true);
    },
    onDoubleClick: () => { setWidthPct(null); saveWidth(null); },
    // Keyboard: ← widens (the pane grows toward the data), → narrows.
    onKeyDown: (e) => {
      const step = e.key === 'ArrowLeft' ? -3 : e.key === 'ArrowRight' ? 3 : 0;
      if (!step) return;
      e.preventDefault();
      const next = clampWidth(currentPct() - step, splitRect()?.width || 0);
      setWidthPct(next);
      saveWidth(next);
    },
  };
  return { widthPct, resizing, handleProps };
}

export default function DocFileViewer({
  files, pageIdx, onPageChange, url, variant, onClose, onDownload,
  onMaximize, onDock, onRotate, offerNativeViewer = false,
  // The page failed to load: say so in the body and offer `onRetry`, while the
  // toolbar (page arrows, מסך מלא) stays — one bad page must not hide the rest.
  error = false, onRetry,
}) {
  const fullscreen = variant === 'fullscreen';
  const paneRef = useRef(null);
  const { widthPct, resizing, handleProps } = usePaneWidth(paneRef);
  // Through a ref, so a parent passing a fresh arrow each render doesn't
  // re-run the effect below (and flicker the body's overflow lock).
  const onEscapeRef = useRef(onDock || onClose);
  onEscapeRef.current = onDock || onClose;

  // Escape leaves the fullscreen layer, and the page behind it must not
  // scroll while it is up.
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onEscapeRef.current(); };
    window.addEventListener('keydown', onKey);
    // Opened over a Modal (the console's deleted-document viewer) the body is
    // already locked by it. Leave that lock alone: restoring it here after the
    // Modal had released it would freeze the page until a reload.
    const prevOverflow = document.body.style.overflow;
    const alreadyLocked = prevOverflow === 'hidden';
    if (!alreadyLocked) document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      if (!alreadyLocked) document.body.style.overflow = prevOverflow;
    };
  }, [fullscreen]);

  const idx = Math.min(Math.max(0, pageIdx), files.length - 1);
  const cur = files[idx];
  const multi = files.length > 1;
  const pageLabel = multi ? `עמוד ${idx + 1} מתוך ${files.length}` : 'מסמך מקורי';
  const pdf = isPdfPath(cur?.file_path);
  const rotation = pageRotation(cur);

  const toolbar = (
    <div className="dfv-toolbar">
      <span className="dfv-title">
        {/* The word goes on the narrowest phones (≤380px) so six 44px
            buttons still fit beside "1/2". */}
        {multi ? <><span className="dfv-title-word">עמוד </span>{idx + 1}/{files.length}</> : 'מסמך מקורי'}
      </span>
      <div className="dfv-actions">
        {multi && (
          <>
            <button type="button" className="dfv-btn" onClick={() => onPageChange(Math.max(0, idx - 1))}
              disabled={idx === 0} aria-label="העמוד הקודם" title="העמוד הקודם">
              <Icon name="chevron-inline" size={16} flip />
            </button>
            <button type="button" className="dfv-btn" onClick={() => onPageChange(Math.min(files.length - 1, idx + 1))}
              disabled={idx === files.length - 1} aria-label="העמוד הבא" title="העמוד הבא">
              <Icon name="chevron-inline" size={16} />
            </button>
          </>
        )}
        {onRotate && cur && (
          <button type="button" className="dfv-btn" onClick={() => onRotate(cur)}
            aria-label="סיבוב העמוד" title="סיבוב העמוד ב-90° (נשמר לכולם)">
            <Icon name="rotate-cw" size={16} />
          </button>
        )}
        <button type="button" className="dfv-btn" onClick={onDownload} aria-label="הורדת מסמך" title="הורדת מסמך">
          <Icon name="download" size={16} />
        </button>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" className="dfv-btn"
            aria-label="פתיחה בחלון נפרד" title="פתיחה בחלון נפרד">
            <Icon name="external" size={16} />
          </a>
        )}
        {onMaximize && (
          <button type="button" className="dfv-btn dfv-btn--label" onClick={onMaximize} title="מסך מלא">
            <Icon name="maximize" size={16} /> מסך מלא
          </button>
        )}
        {onDock && (
          <button type="button" className="dfv-btn dfv-btn--label" onClick={onDock} title="לצד הנתונים (Esc)">
            <Icon name="side-pane" size={16} /> לצד הנתונים
          </button>
        )}
        {/* Without onClose there is nothing to close it into — the console's
            deleted-document viewer embeds the pane for the dialog's lifetime. */}
        {onClose && (
          <button type="button" className="dfv-btn" onClick={onClose} aria-label="סגירת המסמך המקורי" title="סגירה">
            <Icon name="close" size={16} />
          </button>
        )}
      </div>
    </div>
  );

  const page = error ? (
    <div className="dfv-loading" role="status">
      <span>לא ניתן להציג את העמוד</span>
      {onRetry && <button type="button" className="btn btn-sm btn-secondary" onClick={onRetry}>נסה שוב</button>}
    </div>
  ) : !url ? (
    <div className="dfv-loading"><div className="spinner" /><span>טוען מסמך…</span></div>
  ) : pdf ? (
    // Fit the page to the pane's WIDTH (the hint ReviewDocPane uses) and
    // hide the viewer's thumbnail sidebar — measured in Chrome, the page went
    // from 43% to 79% zoom in the same pane. Without these a wider pane only
    // buys margin around a page-fit PDF.
    <RotatedFrame src={url.includes('#') ? url : `${url}#navpanes=0&view=FitH`} title={pageLabel} className="dfv-frame" rotation={rotation} />
  ) : onMaximize ? (
    // An image page in the pane is itself the way to full screen. (A PDF
    // iframe keeps its own clicks — its מסך מלא is the toolbar button.)
    <button type="button" className="dfv-img-btn" onClick={onMaximize} aria-label="מסך מלא">
      <RotatedImg src={url} alt={pageLabel} className="dfv-img" rotation={rotation} />
    </button>
  ) : (
    <RotatedImg src={url} alt={pageLabel} className="dfv-img" rotation={rotation} />
  );

  if (!fullscreen) {
    return (
      <aside ref={paneRef}
        className={`dfv dfv--pane${resizing ? ' is-resizing' : ''}${widthPct != null ? ' is-sized' : ''}`}
        style={widthPct != null ? { flex: `0 0 ${widthPct}%` } : undefined}>
        <div className="dfv-resize-handle" {...handleProps} />
        {toolbar}
        <div className="dfv-body">{page}</div>
      </aside>
    );
  }
  return (
    <div className="dfv dfv--fullscreen" role="dialog" aria-modal="true" aria-label="מסמך מקורי">
      {toolbar}
      <div className="dfv-body">{page}</div>
      {offerNativeViewer && pdf && url && (
        <a href={url} target="_blank" rel="noreferrer" className="btn btn-primary dfv-native">
          <Icon name="external" size={15} /> פתיחה בחלון נפרד
        </a>
      )}
    </div>
  );
}
